import usePanelState from '@databases/panel/usePanelState';
import { useCallback, useRef } from 'react';

import { MAX_POPOVER_EDIT_WIDTH } from './popover-constants.tsx';

export interface AdjustTextAreaDimensionsProps {
  textAreaRef: React.RefObject<HTMLTextAreaElement>;
  maxWidthReachedRef: React.MutableRefObject<boolean>;
  maxPopoverEditWidth?: number;
  minTextAreaHeight?: number;
  shouldExpandFromCell?: boolean;
}

export const useAdjustTextAreaDimensions = ({
  textAreaRef,
  maxWidthReachedRef,
  maxPopoverEditWidth = MAX_POPOVER_EDIT_WIDTH,
  minTextAreaHeight,
  shouldExpandFromCell = true
}: AdjustTextAreaDimensionsProps) => {
  const { isPanelVisible } = usePanelState();
  const isAnyPanelVisible = isPanelVisible();
  const whiteSpace = isAnyPanelVisible ? 'normal' : 'nowrap';
  const textAreaInitialTopRef = useRef<number | null>(null);

  return useCallback(() => {
    const textArea = textAreaRef.current;

    if (!textArea) {
      textAreaInitialTopRef.current = null;
      return;
    }

    if (textAreaInitialTopRef.current === null) {
      // Capture the original value of the top of the text area before text area resizing occurs
      textAreaInitialTopRef.current = textArea.getBoundingClientRect().top;
    }

    const middleOfScreen = globalThis.innerHeight / 2;

    const computedStyle = window.getComputedStyle(textArea);
    const paddingTop = parseFloat(computedStyle.paddingTop);
    const paddingBottom = parseFloat(computedStyle.paddingBottom);
    const paddingTotal = paddingTop + paddingBottom;
    // `scrollHeight` includes padding calculation, which needs to be removed
    const textAreaHeight = textArea.scrollHeight - paddingTotal;
    textArea.style.height = `${
      minTextAreaHeight === undefined
        ? textAreaHeight
        : Math.max(minTextAreaHeight, textAreaHeight)
    }px`;
    textArea.style.minHeight =
      minTextAreaHeight === undefined ? '100%' : `${minTextAreaHeight}px`;
    textArea.style.minWidth = 'auto';
    textArea.style.whiteSpace = whiteSpace;

    if (!shouldExpandFromCell) {
      return;
    }

    // Keeps characters from hitting the edge of the text area
    const textAreaWidthBuffer = 15;
    const parentWidth = textArea.parentElement?.clientWidth || 0;
    let width = Math.min(textArea.scrollWidth, maxPopoverEditWidth);
    if (textArea.scrollWidth >= parentWidth) {
      // Content is touching edge, add buffer
      width = Math.min(textArea.scrollWidth + textAreaWidthBuffer, maxPopoverEditWidth);
    }

    textArea.style.minWidth = `${width}px`;

    if (width >= maxPopoverEditWidth && !maxWidthReachedRef.current) {
      maxWidthReachedRef.current = true;
    } else if (width < maxPopoverEditWidth && maxWidthReachedRef.current) {
      maxWidthReachedRef.current = false;
    }

    // when we text wrap, max width is already reached, so the text area does
    // not need to expand any further
    if (maxWidthReachedRef.current || whiteSpace === 'normal') {
      const textAreaContainerHeight = textArea.parentElement?.clientHeight || 0;
      textArea.style.whiteSpace = 'normal';
      // Text area height will retain cell height at a minimum and expand vertically
      textArea.style.height = `${Math.max(
        textAreaContainerHeight,
        textArea.scrollHeight
      )}px`;

      // Text area opens "downwards" if above middle of screen and "upwards" if below
      if (textAreaInitialTopRef.current > middleOfScreen) {
        textArea.style.bottom = '0px';
      } else {
        textArea.style.top = '0px';
      }
    }
  }, [
    maxPopoverEditWidth,
    maxWidthReachedRef,
    minTextAreaHeight,
    shouldExpandFromCell,
    textAreaRef,
    whiteSpace
  ]);
};
